/**
 * Type-to-filter country picker.
 *
 * A plain Select over ~250 countries means scrolling; free text means a typo silently
 * changes the shipping rate (country decides US-domestic vs international). This is the
 * middle ground: type to narrow, pick to commit an ISO code.
 *
 * Hand-rolled rather than composed from `@staff-app/ui` because the package carries no
 * Command/Combobox primitive, and scio3's MultiSelectCombobox is off-limits — ESLint
 * blocks `@scio3/*` imports so the two apps can't re-couple. Radix `Popover` was the other
 * option but it moves focus into the panel on open, which fights typing in the trigger;
 * an anchored listbox keeps the caret in the input where it belongs.
 *
 * Unmatched text is KEPT rather than rejected: the engine infers the shipping region from
 * the raw string, so an unusual spelling still prices. The field just marks it as
 * unrecognised so the operator can see it didn't resolve to a known country.
 */
import { useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Input, cn } from '@staff-app/ui';
import { countryLabel, countryOptions, isKnownCountry, normalizeCountry } from '../countries';

export interface CountryComboboxProps {
  /** ISO alpha-2 code, or raw text that didn't resolve. */
  value?: string;
  onChange: (value: string) => void;
  testId?: string;
  /**
   * Restrict the pick list to these ISO codes. Omit for all ~250 — which is what the Quote
   * Generator wants, since it prices anywhere.
   *
   * International Requests passes the 29 countries Amazon MCF can actually ship to. Offering a
   * rep a country the lane cannot serve is offering them a request that gets refused, and the
   * ship-to country is the ONE field the update mutation will not change afterwards.
   */
  codes?: readonly string[];
  /**
   * What to say when the typed text matches a real country that `codes` excludes.
   *
   * THE COST OF A SHORT LIST, PAID BACK. Hiding countries silently turns "Australia is not
   * offered" into "the picker is broken" — the rep types a country they know exists and gets an
   * empty panel with no reason. The copy lives at the call site because only the caller knows
   * WHY its list is short.
   */
  outsideNote?: (label: string) => string;
}

/** Prefix matches rank above substring matches, so typing "ind" offers India before Indonesia. */
function rank(label: string, code: string, q: string): number | null {
  const l = label.toLowerCase();
  if (code.toLowerCase() === q) return 0;
  if (l.startsWith(q)) return 1;
  if (l.includes(q)) return 2;
  return null;
}

export function CountryCombobox({
  value,
  onChange,
  testId,
  codes,
  outsideNote,
}: CountryComboboxProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  /** null = not editing; show the resolved label instead of the draft text. */
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const known = isKnownCountry(value);
  const shown = draft ?? (known ? countryLabel(value as string) : (value ?? ''));

  /** The allowed set, or null for "everything". Built once so the filter is a Set lookup. */
  const allowed = useMemo(
    () => (codes ? new Set(codes.map((c) => c.toUpperCase())) : null),
    [codes],
  );

  /** Everything the query matches, BEFORE the `codes` filter — see `outsideMatch`. */
  const queryMatches = useMemo(() => {
    const all = countryOptions();
    const q = (draft ?? '').trim().toLowerCase();
    if (!q) return all;
    return all
      .map((c) => ({ c, r: rank(c.label, c.code, q) }))
      .filter((x): x is { c: (typeof all)[number]; r: number } => x.r !== null)
      .sort((a, b) => a.r - b.r || a.c.label.localeCompare(b.c.label))
      .map((x) => x.c);
  }, [draft]);

  const matches = useMemo(
    () => (allowed ? queryMatches.filter((c) => allowed.has(c.code)) : queryMatches),
    [queryMatches, allowed],
  );

  /**
   * The country the rep typed that IS real and IS excluded — the one case an empty panel would
   * otherwise fail to explain. Only the first, because naming one is enough to answer "why is it
   * not in the list" and a list of near-misses answers nothing.
   */
  const outsideMatch =
    allowed && matches.length === 0 && queryMatches.length > 0 ? queryMatches[0] : null;

  const commit = (code: string) => {
    onChange(code);
    setDraft(null);
    setOpen(false);
  };

  /** Leaving the field resolves whatever was typed, keeping it as-is when nothing matches. */
  const commitDraft = () => {
    if (draft === null) return;
    const text = draft.trim();
    if (!text) {
      onChange('');
    } else {
      const resolved = normalizeCountry(text);
      // An EXCLUDED country resolves to nothing. Committing `AU` here because the text said
      // Australia would put a code in the field that the list deliberately does not offer, and
      // the form would then refuse it with a message about the code rather than about Australia.
      const usable = isKnownCountry(resolved) && (!allowed || allowed.has(resolved as string));
      onChange(usable ? (resolved as string) : matches.length === 1 ? matches[0].code : text);
    }
    setDraft(null);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={shown}
          placeholder="Start typing a country…"
          data-testid={testId}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setOpen(true);
              setActive((i) => {
                const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
                return Math.max(0, Math.min(matches.length - 1, next));
              });
              return;
            }
            if (e.key === 'Enter') {
              if (open && matches[active]) {
                e.preventDefault();
                commit(matches[active].code);
              }
              return;
            }
            if (e.key === 'Escape') {
              setDraft(null);
              setOpen(false);
            }
          }}
          className="pr-8"
        />
        <ChevronsUpDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>

      {open && outsideMatch && outsideNote ? (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-2 text-xs text-muted-foreground shadow-md"
          data-testid={testId ? `${testId}-outside` : undefined}
        >
          {outsideNote(outsideMatch.label)}
        </div>
      ) : null}

      {open && matches.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Country"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {matches.map((c, i) => (
            <li
              key={c.code}
              role="option"
              aria-selected={c.code === value}
              // mousedown, not click: click fires after blur, which would have already
              // committed the draft and closed the list out from under the pointer.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(c.code);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                'flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm',
                i === active ? 'bg-accent text-accent-foreground' : 'text-foreground',
              )}
            >
              <span>{c.label}</span>
              {c.code === value ? <Check className="h-3.5 w-3.5" /> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {value && !known ? (
        <p className="mt-1 text-[11px] text-warning" data-testid={testId ? `${testId}-unknown` : undefined}>
          {`"${value}" isn't a country we recognise — it will be treated as international shipping.`}
        </p>
      ) : null}
    </div>
  );
}
